import React, { FC, useRef, useState } from "react";
import cx from "classnames";
import {
  AddButton,
  ClickOutside,
  InputGroup,
  isEnterKey,
  Paragraph,
} from "@smartsuite/react-ui/lib";
import { TextColorNames } from "@smartsuite/types";
import PhoneInput, { Country, isValidPhoneNumber } from "react-phone-number-input";
import { E164Number } from "libphonenumber-js/types";

import { $t } from "../../../../../utils/intl";
import { fieldsText } from "../../../fields.text";

import "./AddPhone.sass";
import { validatorText } from "../../../../validator.text";

export interface AddPhoneProps {
  defaultCountry: Country;
  forceAdding?: boolean;
  readOnly?: boolean;
  onChangeEnteredValue?: (value?: E164Number) => void;
  onAddNewPhone: (value: E164Number) => void;
}

export const AddPhone: FC<AddPhoneProps> = ({
  defaultCountry,
  forceAdding,
  readOnly,
  onChangeEnteredValue,
  onAddNewPhone,
}) => {
  const inputRef = useRef(null);
  const [value, setValue] = useState<E164Number | undefined>(undefined);
  const [showError, setShowError] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const handleAddPhone = (): void => {
    if (value && !showError) {
      onAddNewPhone(value);
      setValue(undefined);
      setIsAdding(false);
    }
  };

  const handleChange = (newValue?: E164Number): void => {
    if (newValue?.length) {
      setValue(newValue);
      const isValid = isValidPhoneNumber(newValue);
      setShowError(!isValid);

      // If the number is valid, add it immediately
      if (isValid) {
        onAddNewPhone(newValue);
        setValue(undefined);
        setIsAdding(false);
      }
    } else {
      setValue(undefined);
      setShowError(false);
    }

    if (onChangeEnteredValue) {
      onChangeEnteredValue(newValue);
    }
  };

  const setAddingMode = (): void => {
    setIsAdding(true);

    setTimeout(() => {
      if (inputRef.current) {
        const input = inputRef.current as HTMLInputElement;
        input.focus();
      }
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (isEnterKey(event)) {
      event.preventDefault();
      handleAddPhone();
    }
  };

  const inputClassName = cx("", {
    "add-phone-element": !forceAdding,
  });

  return (
    <ClickOutside onClickOutside={handleAddPhone}>
      {showError || isAdding || forceAdding ? (
        <div>
          <InputGroup className={inputClassName} state={showError ? "error" : "default"}>
            <PhoneInput
              disabled={readOnly}
              data-testid="phone-input"
              ref={inputRef}
              countryCallingCodeEditable={false}
              defaultCountry={defaultCountry}
              placeholder={$t(fieldsText.phonePlaceholder)}
              value={value}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
            />
          </InputGroup>
          {showError && (
            <div className="add-phone-element__error-message">
              <Paragraph color="error" size="s">
                {$t(validatorText.validationPhone)}
              </Paragraph>
            </div>
          )}
        </div>
      ) : (
        <AddButton
          className="add-phone-element"
          textStyle={{
            size: "xs",
            weight: "semibold",
            color: TextColorNames.primary,
            hoverColor: TextColorNames.primary,
          }}
          iconStyle={{ marginRight: 6 }}
          onClick={setAddingMode}
        >
          Add Phone Number
        </AddButton>
      )}
    </ClickOutside>
  );
};
